/* Theme state + Monaco color customization — persists to localStorage. */

const LS_THEME = 'agentcpq_theme';
const LS_EDITOR_COLORS = 'agentcpq_editor_colors';

/**
 * Token groups exposed to the user for customization.
 * `id` matches the key in the persisted color object.
 * `monacoTokens` are the Monaco scope strings each group maps to.
 */
export const EDITOR_COLOR_GROUPS = [
  { id: 'keyword',  label: 'Keywords',    sample: 'if, for, def',     monacoTokens: ['keyword'],              defaultLight: '#0070F2', defaultDark: '#4F9CFF' },
  { id: 'string',   label: 'Strings',     sample: '"hello"',          monacoTokens: ['string'],               defaultLight: '#188918', defaultDark: '#8BDD8B' },
  { id: 'number',   label: 'Numbers',     sample: '42',               monacoTokens: ['number'],               defaultLight: '#E76500', defaultDark: '#FFAD66' },
  { id: 'builtin',  label: 'Builtins',    sample: 'len, range, True', monacoTokens: ['type.identifier'],      defaultLight: '#9B4DCA', defaultDark: '#D69AFF' },
  { id: 'cpq',      label: 'CPQ Objects', sample: 'Quote, Item, Trace', monacoTokens: ['variable.predefined'], defaultLight: '#CC1919', defaultDark: '#FF7A7A' },
  { id: 'comment',  label: 'Comments',    sample: '# todo',           monacoTokens: ['comment'],              defaultLight: '#6A7A8C', defaultDark: '#8D9AAA' },
];

/** Theme-aware default palette for groups the user hasn't customized. */
function _defaultsFor(theme) {
  const key = theme === 'dark' ? 'defaultDark' : 'defaultLight';
  return Object.fromEntries(EDITOR_COLOR_GROUPS.map(g => [g.id, g[key]]));
}

// ---------------------------------------------------------------------------
// Theme (light/dark/auto)
// ---------------------------------------------------------------------------

export function getTheme() {
  return localStorage.getItem(LS_THEME) || 'auto';
}

export function getEffectiveTheme() {
  const t = getTheme();
  if (t === 'auto') {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return t === 'dark' ? 'dark' : 'light';
}

export function setTheme(name) {
  if (!['light', 'dark', 'auto'].includes(name)) return;
  if (localStorage.getItem(LS_THEME) === name) return;  // no-op guard
  localStorage.setItem(LS_THEME, name);
  _applyTheme();
  _dispatchThemeChange();
}

function _applyTheme() {
  const effective = getEffectiveTheme();
  document.documentElement.dataset.theme = effective;
}

function _dispatchThemeChange() {
  window.dispatchEvent(new CustomEvent('themechange', {
    detail: { theme: getEffectiveTheme() },
  }));
}

// ---------------------------------------------------------------------------
// Editor token colors
// ---------------------------------------------------------------------------

export function getEditorColors() {
  const defaults = _defaultsFor(getEffectiveTheme());
  try {
    const raw = localStorage.getItem(LS_EDITOR_COLORS);
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch {
    return defaults;
  }
}

export function setEditorColorsAll(colors) {
  const existing = localStorage.getItem(LS_EDITOR_COLORS);
  const next = JSON.stringify(colors);
  if (existing === next) return;  // no-op guard
  localStorage.setItem(LS_EDITOR_COLORS, next);
  _dispatchEditorColorsChange();
}

export function resetEditorColors() {
  localStorage.removeItem(LS_EDITOR_COLORS);
  _dispatchEditorColorsChange();
}

function _dispatchEditorColorsChange() {
  window.dispatchEvent(new CustomEvent('editorcolorschange', {
    detail: { colors: getEditorColors() },
  }));
}

// ---------------------------------------------------------------------------
// Monaco theme definition
// ---------------------------------------------------------------------------

/**
 * Build a Monaco theme rules array from the current custom colors,
 * respecting the active effective theme for un-customized groups.
 */
export function buildMonacoThemeRules() {
  const colors = getEditorColors();  // already theme-aware
  const rules = [];
  for (const g of EDITOR_COLOR_GROUPS) {
    const hex = String(colors[g.id]).replace(/^#/, '');
    for (const token of g.monacoTokens) {
      rules.push({ token, foreground: hex });
    }
  }
  return rules;
}

// ---------------------------------------------------------------------------
// Init (called once on boot, before Monaco loads)
// ---------------------------------------------------------------------------

export function initTheme() {
  _applyTheme();

  // Respond to system theme changes when user has selected 'auto'
  if (window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => {
      if (getTheme() === 'auto') {
        _applyTheme();
        _dispatchThemeChange();
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', listener);
    else if (mq.addListener) mq.addListener(listener);  // Safari <14
  }
}
